import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {defineConfig, createSystem, ChakraProvider, defaultBaseConfig, Box} from '@chakra-ui/react3'

import {animationStyles} from './animation-styles'
import {breakpoints} from './breakpoints'
import {globalCss} from './global-css'
import {keyframes} from './keyframes'
import {layerStyles} from './layer-styles'
import {recipes} from './recipes'
import {semanticTokens} from './semantic-tokens'
import {slotRecipes} from './slot-recipes'
import {textStyles} from './text-styles'
import {tokens} from './tokens'

const config = defineConfig({
    cssVarsPrefix: 'leap',
    cssVarsRoot: ':where(html, .chakra-theme)',
    disableLayers: true, // required to allow recipes to work while chakra2 is also used
    preflight: true,
    conditions: {
        icon: '& :where(svg), & :where(.icon)', // use .icon for non-svg icons
        // Matches `cssVarsRoot` below: bare `html` carries the page's default
        // theme (e.g. via `data-leap-theme` set through `Helmet`/`Seo`), while
        // `.chakra-theme` wraps a subtree that opts into a different one.
        // `data-leap-theme`, not `data-theme`: the latter is already owned by
        // Chakra v2's own theme attribute on `html`, and colliding with it
        // would let either system flip the other's styling.
        light: '*:is(html, .chakra-theme)[data-leap-theme=light] &',
        dark: '*:is(html, .chakra-theme)[data-leap-theme=dark] &',
        hasLeftIcon: '&:has(> svg:first-child, > .icon:first-child)', // use .icon for non-svg icons
        hasRightIcon: '&:has(> svg:last-child, > .icon:last-child)', // use .icon for non-svg icons
        hasOnlyIcon: '&:has(> svg:only-child, > .icon:only-child)' // use .icon for non-svg icons
    },
    globalCss,
    theme: {
        breakpoints,
        keyframes,
        tokens,
        semanticTokens,
        recipes,
        slotRecipes,
        textStyles,
        layerStyles,
        animationStyles
    }
})
const system = createSystem(defaultBaseConfig, config)

// an override to the default config that only uses mobile breakpoints for use in
// small containers like drawer
const mobileOnlyConfig = defineConfig({
    ...config,
    preflight: false,
    globalCss: false,
    theme: {
        ...config.theme,
        breakpoints: {
            ...Object.fromEntries(
                Object.keys(config.theme.breakpoints).map((key) => [key, '99999em'])
            ),
            base: '0px',
            xxs: '375px'
        }
    }
})
const mobileOnlySystem = createSystem(defaultBaseConfig, mobileOnlyConfig)

const THEME_SCOPE_SELECTOR = ':is(html, .chakra-theme)[data-leap-theme]'

/**
 * Reads the theme in effect for the element the returned ref is attached to, by walking up
 * to the nearest themed scope -- a `Chakra3Theme` wrapper, or `<html>` for the page's own
 * theme.
 *
 * Only for values that must cross out of CSS -- styling our own elements light/dark goes
 * through the `_light`/`_dark` conditions, which need no JavaScript.
 *
 * @returns {[('light'|'dark'), function(HTMLElement|null): void]} The active theme
 *   (`light` outside any themed scope) and the ref to put on an element inside the subtree.
 */
export const useLeapTheme = () => {
    const [element, setElement] = useState(null)
    const [theme, setTheme] = useState('light')

    useEffect(() => {
        if (!element) {
            setTheme('light')
            return
        }

        const readTheme = () => {
            const scope = element.closest(THEME_SCOPE_SELECTOR)
            setTheme(scope?.dataset.leapTheme === 'dark' ? 'dark' : 'light')
        }
        readTheme()

        const observer = new MutationObserver(readTheme)
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-leap-theme'],
            subtree: true
        })

        return () => observer.disconnect()
    }, [element])

    return [theme, setElement]
}

/**
 * Theme wrapper for Chakra v3 sections.
 *
 * Applies the theme through the `chakra-theme` class and `data-leap-theme`
 * attribute, allowing nested content to be rendered in either light or dark
 * mode.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Content to render inside the themed wrapper.
 * @param {'light'|'dark'} [props.theme='light'] - Theme variant applied to the wrapper.
 * @returns {JSX.Element} The themed wrapper element.
 */
export const Chakra3Theme = ({children, theme = 'light'}) => {
    return (
        <Box className="chakra-theme" data-leap-theme={theme} display="contents">
            {children}
        </Box>
    )
}

Chakra3Theme.propTypes = {
    children: PropTypes.node,
    theme: PropTypes.oneOf(['light', 'dark'])
}

const Chakra3Provider = ({children, theme = 'light'}) => {
    return (
        <ChakraProvider value={system}>
            <Chakra3Theme theme={theme}>{children}</Chakra3Theme>
        </ChakraProvider>
    )
}

Chakra3Provider.propTypes = {
    children: PropTypes.node,
    theme: PropTypes.oneOf(['light', 'dark'])
}

export const Chakra3MobileOnlyProvider = ({children}) => {
    return <ChakraProvider value={mobileOnlySystem}>{children}</ChakraProvider>
}

Chakra3MobileOnlyProvider.propTypes = {
    children: PropTypes.node
}

export default Chakra3Provider
